import { Badge, Button, Card, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { api, call, type Schemas } from "../api/client";
import { kcal } from "../lib/format";

const ACTION_COLORS: Record<string, string> = { create: "teal", update: "blue", upsert: "blue", delete: "red" };

function describe(e: Schemas["AuditOut"]): string {
  const d = e.data as Record<string, unknown> | unknown[] | null;
  if (Array.isArray(d)) return `${d.length} health value${d.length === 1 ? "" : "s"}`;
  if (!d) return e.entity_id;
  if (e.entity === "log_entry") return `${d.name} · ${kcal(d.kcal as number)} kcal · ${d.date} ${d.meal}`;
  if (e.entity === "food") return `${d.name} · ${kcal(d.kcal as number)} kcal per ${d.serving_name}`;
  if (e.entity === "goal") return `${kcal(d.kcal as number)} kcal from ${d.effective_from}`;
  if (e.entity === "health_metric") return `${d.metric} = ${d.value} on ${d.date}`;
  return Object.keys(d).join(", ");
}

export function ActivityPage() {
  const tokens = useQuery({ queryKey: ["tokens"], queryFn: () => call(api.GET("/api/v1/tokens")) });
  const tokenName = (id: number | null) =>
    id == null ? "You (web)" : (tokens.data?.find((t) => t.id === id)?.name ?? `token #${id}`);
  const events = useInfiniteQuery({
    queryKey: ["audit"],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) =>
      call(api.GET("/api/v1/audit", { params: { query: { limit: 50, before_id: pageParam ?? null } } })),
    getNextPageParam: (last) => (last.length === 50 ? last[last.length - 1]!.id : undefined),
  });

  return (
    <Stack>
      <Title order={3}>Activity</Title>
      <Text size="sm" c="dimmed">
        Every change to your data, and whether you or one of your API tokens made it.
      </Text>
      {events.isPending && <Loader />}
      {events.data?.pages.flat().map((e) => (
        <Card key={e.id} withBorder padding="xs">
          <Group justify="space-between" wrap="nowrap" align="flex-start">
            <Stack gap={2} style={{ minWidth: 0 }}>
              <Group gap="xs">
                <Badge size="xs" color={ACTION_COLORS[e.action] ?? "gray"} variant="light">
                  {e.action}
                </Badge>
                <Text size="sm" fw={500}>
                  {e.entity.replace("_", " ")}
                </Text>
              </Group>
              <Text size="sm" truncate>
                {describe(e)}
              </Text>
            </Stack>
            <Stack gap={0} align="flex-end" style={{ flexShrink: 0 }}>
              <Text size="xs">{tokenName(e.api_token_id)}</Text>
              <Text size="xs" c="dimmed">
                {dayjs(e.at + "Z").format("D MMM HH:mm")}
              </Text>
            </Stack>
          </Group>
        </Card>
      ))}
      {events.hasNextPage && (
        <Button variant="subtle" onClick={() => events.fetchNextPage()} loading={events.isFetchingNextPage}>
          Load more
        </Button>
      )}
    </Stack>
  );
}
